import { useMemo } from "react";
import { Check, Quote } from "lucide-react";

import {
  MockGenieConfig,
  PersonalityDimension,
  PersonalityState,
} from "@/types/mockups/genie-setup-v2";

import { personalityDimensions, personalityPresets } from "../data";
import { simulateResponse } from "../composer";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface Step2PersonalityProps {
  config: MockGenieConfig;
  onChange: (config: MockGenieConfig) => void;
}

export const Step2Personality = ({ config, onChange }: Step2PersonalityProps) => {
  const setPersonality = (next: PersonalityState) => {
    onChange({ ...config, personality: next });
  };

  const handleOptionSelect = (dim: PersonalityDimension, optionId: string) => {
    setPersonality({ ...config.personality, [dim.id]: optionId });
  };

  const applyPreset = (presetId: string) => {
    const preset = personalityPresets.find((p) => p.id === presetId);
    if (preset) setPersonality(preset.values);
  };

  const previewLine = useMemo(() => {
    const anyPicked = Object.values(config.personality).some((v) => v !== null);
    if (!anyPicked) return null;
    return simulateResponse("How do I get started?", config);
  }, [config]);

  return (
    <div className="space-y-8">
      {/* Presets row */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <GenieIcon name="eye" size={20} className="h-5 w-5" />
          <h3 className="text-sm font-semibold text-gray-900">
            Start with a personality — or build your own below
          </h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {personalityPresets.map((preset) => {
            const isActive =
              preset.values.role === config.personality.role &&
              preset.values.length === config.personality.length &&
              preset.values.energy === config.personality.energy &&
              preset.values.approach === config.personality.approach &&
              preset.values.formality === config.personality.formality;

            const inner = (
              <div className={`relative ${isActive ? "rounded-[10px] bg-white" : "rounded-xl"} p-4 h-full`}>
                {isActive && (
                  <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-genie-primary flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
                <GenieIcon name={preset.genieIcon} size={32} className="h-8 w-8 mb-2" />
                <div className="text-sm font-semibold text-gray-900">{preset.label}</div>
                <div className="text-xs text-gray-500 mt-1">{preset.description}</div>
              </div>
            );

            if (isActive) {
              return (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => applyPreset(preset.id)}
                  className="text-left p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] transition-all"
                >
                  {inner}
                </button>
              );
            }

            return (
              <button
                key={preset.id}
                type="button"
                onClick={() => applyPreset(preset.id)}
                className="text-left rounded-xl border-2 border-gray-200 bg-white hover:border-genie-primary/40 hover:shadow-md transition-all"
              >
                {inner}
              </button>
            );
          })}
        </div>
      </section>

      {/* Live preview banner */}
      <section>
        {previewLine ? (
          <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
            <div className="rounded-[14px] bg-white p-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-genie-primary/10 text-genie-primary flex items-center justify-center shrink-0">
                  <Quote className="w-4 h-4" />
                </div>
                <div className="flex-1">
                  <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
                    Your genie will sound like
                  </div>
                  <p className="text-base text-gray-900 italic leading-relaxed">
                    "{previewLine}"
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    In response to: <span className="italic">"How do I get started?"</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-5 flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center shrink-0">
              <Quote className="w-4 h-4" />
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
                Your genie will sound like
              </div>
              <p className="text-sm text-gray-500 italic">
                Pick a personality below and we'll show you exactly how your genie will respond.
              </p>
            </div>
          </div>
        )}
      </section>

      {/* Dimensions */}
      <section className="space-y-5">
        {personalityDimensions.map((dim) => {
          const Icon = dim.icon;
          const selectedOptionId = config.personality[dim.id];
          return (
            <div
              key={dim.id}
              className="bg-white rounded-2xl border border-gray-200 p-5"
            >
              <div className="flex items-start gap-3 mb-4">
                <div className="w-9 h-9 rounded-lg bg-genie-primary/10 text-genie-primary flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-gray-900">{dim.question}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">{dim.helper}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {dim.options.map((option) => {
                  const isSelected = selectedOptionId === option.id;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => handleOptionSelect(dim, option.id)}
                      className={`text-left p-3 rounded-xl border-2 transition-all ${
                        isSelected
                          ? "border-genie-primary bg-genie-primary/5"
                          : "border-gray-200 bg-white hover:border-genie-primary/40"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-semibold text-gray-900">
                          {option.label}
                        </span>
                        {isSelected && (
                          <div className="w-4 h-4 rounded-full bg-genie-primary flex items-center justify-center shrink-0">
                            <Check className="w-2.5 h-2.5 text-white" />
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mb-2">{option.description}</p>
                      <div className="text-[11px] text-gray-700 italic bg-gray-50 rounded-md px-2 py-1.5 leading-snug">
                        "{option.examplePreview}"
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </section>
    </div>
  );
};
